import { ROLES } from '../constants/roles';

export interface HouseViewer {
  id?: string;
  role?: string;
  houseId?: string;
  houseIds?: string[];
}

const STORAGE_PREFIX = 'activeHouse:';
const GLOBAL_HOUSE_SCOPE = '';

export function isGlobalPlatformViewer(viewer: HouseViewer | null | undefined): boolean {
  return viewer?.role === ROLES.GOD;
}

export function isDefaultSupportAgent(viewer: HouseViewer | null | undefined): boolean {
  return viewer?.role === ROLES.GOD || viewer?.role === ROLES.SUPPORT_AGENT;
}

export function usesGlobalDataScope(
  viewer: HouseViewer | null | undefined,
  activeHouseId = GLOBAL_HOUSE_SCOPE,
): boolean {
  return isGlobalPlatformViewer(viewer) && !activeHouseId;
}

/** Houses a user may scope to. Default: one house; optional houseIds for multi-house staff. */
export function getManageableHouseIds(viewer: HouseViewer | null | undefined): string[] {
  if (!viewer || isGlobalPlatformViewer(viewer)) return [];

  const multiHouseRoles = [
    ROLES.BANKER,
    ROLES.SUPPORT_AGENT,
    ROLES.LISTER,
    ROLES.RUNNER,
  ] as string[];

  if (multiHouseRoles.includes(viewer.role ?? '')) {
    const fromList = Array.isArray(viewer.houseIds)
      ? viewer.houseIds.filter(Boolean)
      : [];
    if (fromList.length > 0) return [...new Set(fromList)];
    return viewer.houseId ? [viewer.houseId] : [];
  }

  return viewer.houseId ? [viewer.houseId] : [];
}

export function canAccessHouse(viewer: HouseViewer | null | undefined, houseId: string): boolean {
  if (!viewer || !houseId) return false;
  if (isGlobalPlatformViewer(viewer)) return true;
  return getManageableHouseIds(viewer).includes(houseId);
}

export function readStoredActiveHouse(userId: string): string | null {
  try {
    return localStorage.getItem(`${STORAGE_PREFIX}${userId}`);
  } catch {
    return null;
  }
}

export function resolveInitialActiveHouse(
  viewer: HouseViewer | null | undefined,
  storedId: string | null = null,
): string {
  if (isGlobalPlatformViewer(viewer)) {
    return storedId ?? GLOBAL_HOUSE_SCOPE;
  }
  const ids = getManageableHouseIds(viewer);
  if (storedId && ids.includes(storedId)) return storedId;
  if (viewer?.houseId && ids.includes(viewer.houseId)) return viewer.houseId;
  return ids[0] ?? GLOBAL_HOUSE_SCOPE;
}

export function persistActiveHouse(userId: string, houseId: string): void {
  try {
    localStorage.setItem(`${STORAGE_PREFIX}${userId}`, houseId);
  } catch {
    // ignore quota / private mode
  }
}

export { GLOBAL_HOUSE_SCOPE };
